<template>
  <app-page
    :title="$t('employee.optOut.title')"
    :error="error"
    :previous="[
      { label: $t('employee.previousLabel'), to: { name: 'employees' } },
      {
        label: employee ? employee.full_name : $t('employee.title'),
        to: { name: 'employee', params: { employeeId: $route.params.employeeId } },
      },
    ]"
    icon="user-minus"
  >
    <app-loading :loading="loading" />
    <template v-if="!loading && !success && employee">
      <!-- Fail-closed: we could not establish whether the employee still holds products, so
           opting out is unavailable rather than allowed by default. -->
      <sme-alert v-if="eligibilityError" level="danger" class="mb-3" data-testid="optout-eligibility-error">
        <i18n path="employee.optOut.eligibilityErrorAlert" tag="span">
          <template #contactSupport>
            <a href="#" @click.prevent="contactSupport">{{ $t('employee.optOut.contactSupportLink') }}</a>
          </template>
        </i18n>
      </sme-alert>

      <template v-else-if="blocked">
        <sme-alert level="warning" class="mb-3" data-testid="optout-blocked">
          <p>
            <strong>{{ $t('employee.optOut.blockedTitle', { name: employee.full_name }) }}</strong>
          </p>
          <p>
            {{ $t('employee.optOut.blockedBody', { partnershipName }) }}
          </p>
        </sme-alert>

        <p v-if="canRecordLeaver">
          <i18n path="employee.optOut.markAsLeaverBody" tag="span">
            <template #name>{{ employee.full_name }}</template>
            <template #markAsLeaverLink>
              <router-link :to="{ name: 'employee-terminate', params: { employeeId: employee.employee_id } }">{{
                $t('employee.optOut.markAsLeaverLink')
              }}</router-link>
            </template>
          </i18n>
        </p>
        <p v-else>
          <i18n path="employee.optOut.contactSupportInsteadBody" tag="span">
            <template #contactSupport>
              <a href="#" @click.prevent="contactSupport">{{ $t('employee.optOut.contactSupportLink') }}</a>
            </template>
            <template #name>{{ employee.full_name }}</template>
          </i18n>
        </p>

        <sme-alert v-if="!canRecordLeaver && canPauseEmployee(employee)" level="info" class="mb-0">
          <i18n path="employee.optOut.pauseInsteadInfoAlert" tag="span">
            <template #name>{{ employee.full_name }}</template>
            <template #pauseLink>
              <router-link :to="{ name: 'employee-pause', params: { employeeId: employee.employee_id } }">{{
                $t('employee.optOut.pauseAccessLink')
              }}</router-link>
            </template>
          </i18n>
        </sme-alert>
      </template>

      <template v-else>
        <sme-alert level="warning" class="mb-3">
          {{ $t('employee.optOut.warningAlert', { name: employee.full_name }) }}
        </sme-alert>

        <i18n path="employee.optOut.loseAccessBody" tag="p" class="text-muted">
          <template #name>{{ employee.full_name }}</template>
          <template #partnershipName>{{ partnershipName }}</template>
          <template #cannotLogIn
            ><strong>{{ $t('employee.optOut.cannotLogInBold') }}</strong></template
          >
          <template #mayLoseProducts
            ><strong>{{ $t('employee.optOut.mayLoseProductsBold', { partnershipName }) }}</strong></template
          >
        </i18n>
      </template>

      <sme-alert v-if="canPauseEmployee(employee) && !blocked" level="info" class="mb-3">
        <i18n path="employee.optOut.pauseAlternativeBody" tag="span">
          <template #name>{{ employee.full_name }}</template>
          <template #pauseLink>
            <router-link :to="{ name: 'employee-pause', params: { employeeId: employee.employee_id } }">{{
              $t('employee.optOut.pauseAccessLink')
            }}</router-link>
          </template>
        </i18n>
      </sme-alert>

      <ValidationObserver v-if="!blocked && !eligibilityError" v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(onSubmit)">
          <app-input
            v-model="reason"
            type="text"
            name="reason"
            :label="$t('employee.optOut.reasonLabel')"
            rules="required"
            :validate-immediate="false"
          />
          <app-input
            v-model="leaver"
            v-if="canDisableEmployees"
            type="checkbox-single"
            name="leaver"
            :label="$t('employee.optOut.leaverCheckboxLabel')"
            rules="required"
            :validate-immediate="false"
          />
          <app-input
            v-if="leaver && !shouldTerminateInstead"
            v-model="terminationDate"
            type="date"
            name="terminationDate"
            :label="$t('employee.optOut.terminationDateLabel')"
            rules="required"
            :validate-immediate="false"
          />

          <!-- If Terminating is enabled, prompt the admin to take this route when applicable -->
          <sme-alert v-if="shouldTerminateInstead" level="info" class="mb-0" data-testid="optout-terminate-instead">
            <p>
              <strong>{{ $t('employee.optOut.markLeaverTitle', { name: employee.full_name }) }}</strong>
            </p>
            <p class="mb-0">
              <i18n path="employee.optOut.terminateInsteadBody" tag="span">
                <template #name>{{ employee.full_name }}</template>
                <template #employmentEnding
                  ><strong>{{ $t('employee.optOut.employmentEndingBold') }}</strong></template
                >
                <template #enterFinalDateLink>
                  <router-link :to="{ name: 'employee-terminate', params: { employeeId: employee.employee_id } }">{{
                    $t('employee.optOut.enterFinalDateLink')
                  }}</router-link>
                </template>
                <template #partnershipName>{{ partnershipName }}</template>
              </i18n>
            </p>
          </sme-alert>
          <page-sub-footer v-else>
            <b-button type="submit" variant="danger" :disabled="v.invalid || saving">
              <b-spinner v-if="saving" class="mr-2" small />
              {{ saving ? $t('employee.optOut.submittingButton') : $t('employee.optOut.submitButton') }}
            </b-button>
          </page-sub-footer>
        </b-form>
      </ValidationObserver>
    </template>
    <div v-if="success">
      <sme-alert level="good">
        <p>
          <strong>{{ $t('employee.optOut.deactivatedTitle') }}</strong>
        </p>
        <p>
          <i18n path="employee.optOut.notifiedBody" tag="span">
            <template #name>{{ employee.full_name }}</template>
            <template #email
              ><a :href="toMailtoHref(employee.work_email)">{{ employee.work_email }}</a></template
            >
          </i18n>
        </p>
      </sme-alert>
      <div v-if="!completedOptOut" class="mt-3">
        <template v-if="readyToRevertBanking">
          <sme-alert level="info" class="mb-3">
            {{ $t('employee.optOut.revertBankingAlert') }}
          </sme-alert>
          <div class="d-flex justify-content-center">
            <router-link
              tag="button"
              class="btn btn-primary"
              :to="{ name: 'employee-revert-banking', params: { employeeId: employee.employee_id } }"
              >{{ $t('employee.actions.completeOptOutButton') }}</router-link
            >
          </div>
        </template>
        <sme-alert v-else level="info">
          {{ $t('employee.optOut.pendingBankingAlert') }}
        </sme-alert>
      </div>
    </div>
  </app-page>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import State from '@/state/State';
import { toMailtoHref } from '@/utils/common';
import { DEFAULT_PARTNERSHIP_NAME, getPartnershipData } from '@/utils/partnership';
import { canPauseEmployee, canTerminateEmployeesAtAll } from '@/utils/Employee';
import { contactSupport } from '@/utils/support';

export default {
  name: 'EmployeeOptOut',
  components: { ValidationObserver, AppInput, AppLoading, AppPage, PageSubFooter, SmeAlert },
  data() {
    return {
      loading: true,
      error: undefined,
      saving: false,
      success: false,
      reason: undefined,
      leaver: false,
      terminationDate: undefined,
      completedOptOut: undefined,
      employee: undefined,
      everEnrolled: undefined,
      eligibility: undefined,
      eligibilityError: undefined,
      state: State.state,
    };
  },
  mounted() {
    this.loadData();
  },
  setup() {
    const { canDisableEmployees } = useFeatureFlags();
    const { name: partnershipName } = getPartnershipData();

    return {
      canDisableEmployees,
      partnershipName: partnershipName || DEFAULT_PARTNERSHIP_NAME,
    };
  },
  methods: {
    toMailtoHref,
    canPauseEmployee,
    contactSupport,
    async onSubmit() {
      this.saving = true;

      try {
        await ApiClient.employeeOptOut(this.employee.employee_id, { reason: this.reason });

        if (!this.everEnrolled) {
          try {
            await ApiClient.employeeOptOutComplete(this.employee.employee_id);
            this.completedOptOut = true;
          } catch (error) {
            this.error = error.message;
          }
        }

        if (this.leaver) {
          try {
            await ApiClient.employeeSetTerminationDate(this.employee.employee_id, this.terminationDate);
          } catch (error) {
            this.error = error.message;
          }
        }
      } catch (error) {
        this.error = error.message;
      }

      await ApiClient.getUser();
      this.success = true;
      this.saving = false;
    },
    async loadData() {
      this.loading = true;
      const employeeId = this.$route.params.employeeId;

      // Page stays in loading state until all three requests settle
      const [employee, eligibility] = await Promise.all([
        ApiClient.getEmployee(employeeId),
        this.checkEligibility(employeeId),
        this.checkifEverEnrolled(employeeId),
      ]).catch(error => {
        if (!this._isDestroyed) {
          this.error = error.message;
        }
        return [];
      });

      // The admin can navigate away while these are in flight, in which case there is no longer an
      // instance to write to.
      if (this._isDestroyed) {
        return;
      }

      this.employee = employee;
      this.eligibility = eligibility;
      this.loading = false;
    },
    // The regulated products this guards against are UK-only, and the eligibility endpoint has no
    // US mirror, so asking would 404 and fail the page closed for every US admin.
    checkEligibility(employeeId) {
      if (!useFeatureFlags().isUkCompany.value) {
        return undefined;
      }

      return ApiClient.getEmployeeOptOutEligibility(employeeId).catch(error => {
        if (!this._isDestroyed) {
          this.eligibilityError = error.message || 'Unable to check opt-out eligibility.';
        }
        return undefined;
      });
    },
    toMailTo(email) {
      return `mailto:${email}`;
    },
    checkifEverEnrolled(employeeId) {
      return ApiClient.checkEmployeeEverEnrolled(employeeId).then(
        everEnrolled => {
          if (!this._isDestroyed) {
            this.everEnrolled = everEnrolled;
          }
        },
        error => {
          if (!this._isDestroyed) {
            this.error = error.message;
          }
        },
      );
    },
  },
  computed: {
    blocked() {
      return Boolean(this.eligibility?.has_regulated_product);
    },
    canRecordLeaver() {
      return canTerminateEmployeesAtAll();
    },
    // When leaver checkbox ticked, push admin to the dedicated Terminate flow if available
    shouldTerminateInstead() {
      return this.leaver && this.canRecordLeaver;
    },
    readyToRevertBanking() {
      if (!this.employee) {
        return false;
      }
      const todos = State.state.todos;
      return todos.some(todo => todo.action === 'employee.revertbanking' && todo.fk === this.employee.employee_id);
    },
  },
};
</script>
